import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BellIcon,
  ChevronDownIcon,
  LogoutIcon,
  MenuIcon,
} from "@heroicons/react/outline";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import { canAccessAction } from "../../routes/routePermissions";
import {
  useNotifications,
  formatTimeAgo,
  AppNotification,
} from "../../hooks/useNotifications";

const formatPageTitle = (pathname: string) => {
  if (pathname.includes("/super-admin/tenants")) return "Tenants Directory";
  if (pathname.includes("/super-admin/plans")) return "Plan Matrix";
  if (pathname.includes("/super-admin")) return "Platform Overview";

  const parts = pathname.split("/").filter(Boolean).slice(1);
  const lastPart = parts[parts.length - 1] || "dashboard";

  if (/^\d+$/.test(lastPart) && parts.length > 1) {
    return parts[parts.length - 2];
  }

  return lastPart
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const Header: React.FC = () => {
  const { user, logout, permissions, roles, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, openSidebar } = useSidebar();
  const [isMobile, setIsMobile] = useState(false);
  const [showProfileOptions, setShowProfileOptions] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const profileOptionsRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    error: notificationsError,
    refresh: refreshNotifications,
    markRead,
    markAllRead,
  } = useNotifications();
  const prevShowNotifications = useRef(false);

  // Fetch fresh notifications whenever the dropdown is opened
  useEffect(() => {
    if (showNotifications && !prevShowNotifications.current) {
      refreshNotifications();
    }
    prevShowNotifications.current = showNotifications;
  }, [showNotifications, refreshNotifications]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileOptionsRef.current && !profileOptionsRef.current.contains(event.target as Node)) {
        setShowProfileOptions(false);
      }

      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pageTitle = useMemo(() => formatPageTitle(location.pathname), [location.pathname]);
  const canOpenSettings = useMemo(() => canAccessAction("configure_settings", permissions, roles, isPlatformAdmin), [permissions, roles, isPlatformAdmin]);
  const userName = user?.name || "Admin User";
  const userInitials = userName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header
      className="fixed right-0 top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200/80 bg-white/95 px-4 backdrop-blur transition-all duration-200 md:px-6"
      style={{ left: isMobile ? 0 : `${sidebarWidth}px` }}
    >
      {/* Left: Breadcrumbs and History Controls */}
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={openSidebar}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 lg:hidden"
          aria-label="Open navigation"
        >
          <MenuIcon aria-hidden="true" className="h-4 w-4 stroke-[2.2]" />
        </button>

        <div className="hidden sm:flex items-center gap-1.5 border border-slate-200 rounded-xl p-1 bg-slate-50/50">
          <button
            type="button"
            onClick={() => window.history.back()}
            title="Go back"
            className="p-1 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-white transition shadow-none hover:shadow-sm"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => window.history.forward()}
            title="Go forward"
            className="p-1 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-white transition shadow-none hover:shadow-sm"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Breadcrumb Path */}
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
          <span className="text-slate-500 hover:text-slate-700 cursor-pointer hidden md:inline" onClick={() => navigate("/admin/dashboard")}>
            Strux
          </span>
          <span className="hidden md:inline">/</span>
          <span className="text-slate-900 font-bold text-sm truncate">{pageTitle}</span>
        </div>
      </div>

      {/* Right Controls */}
      <div className="flex items-center gap-3">
        {/* Help Button */}
        <button
          type="button"
          title="Help & Documentation"
          className="hidden sm:flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-800 text-xs font-bold"
        >
          ?
        </button>

        {/* Notifications */}
        <div className="relative" ref={notificationsRef}>
          <button
            type="button"
            onClick={() => setShowNotifications((current) => !current)}
            className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-800"
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
            aria-haspopup="dialog"
            aria-expanded={showNotifications}
          >
            <BellIcon aria-hidden="true" className="h-4 w-4 stroke-[2.1]" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-black text-white ring-2 ring-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div
              role="dialog"
              aria-label="Notifications"
              className="absolute right-0 mt-2 w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl z-50"
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 bg-slate-50/50">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Notifications</h2>
                  <p className="text-xs text-slate-500 mt-0.5">System updates and reminders.</p>
                </div>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={() => markAllRead()}
                    className="rounded-lg px-2 py-1 text-[11px] font-bold text-emerald-700 transition hover:bg-emerald-50"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {notificationsLoading && notifications.length === 0 ? (
                <div className="px-4 py-8 space-y-3">
                  {[0, 1, 2].map((idx) => (
                    <div key={idx} className="cms-skeleton h-10 w-full rounded-xl" />
                  ))}
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <BellIcon aria-hidden="true" className="mx-auto h-8 w-8 stroke-[1.5] text-slate-300" />
                  <p className="text-xs font-bold text-slate-700 mt-2">All caught up!</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {notificationsError
                      ? "Notifications are unavailable right now."
                      : "No new alerts at this time."}
                  </p>
                </div>
              ) : (
                <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
                  {notifications.slice(0, 8).map((notification: AppNotification) => {
                    const isUnread = !notification.read_at;
                    return (
                      <li key={String(notification.id)}>
                        <button
                          type="button"
                          onClick={() => {
                            if (isUnread) markRead(notification.id);
                          }}
                          className={`flex w-full flex-col gap-0.5 px-4 py-2.5 text-left transition hover:bg-slate-50 ${
                            isUnread ? "bg-emerald-50/40" : ""
                          }`}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="truncate text-xs font-bold text-slate-900">
                              {notification.data?.title || "Notification"}
                              {isUnread && (
                                <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-rose-500 align-middle" />
                              )}
                            </span>
                            <span className="shrink-0 text-[10px] font-semibold text-slate-400">
                              {formatTimeAgo(notification.created_at)}
                            </span>
                          </span>
                          {notification.data?.message && (
                            <span className="line-clamp-2 text-[11px] text-slate-500">
                              {notification.data.message}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* User Profile */}
        <div className="relative" ref={profileOptionsRef}>
          <button
            type="button"
            onClick={() => setShowProfileOptions((current) => !current)}
            className="flex h-10 items-center gap-2.5 rounded-xl border border-slate-200/80 bg-white pl-2 pr-3 shadow-sm transition hover:bg-slate-50"
            aria-label="User menu"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-800 text-xs font-black">
              {userInitials || "U"}
            </div>
            <div className="hidden sm:flex flex-col text-left">
              <span className="text-xs font-bold text-slate-900 max-w-[110px] truncate leading-tight">{userName}</span>
              <span className="text-[10px] font-medium text-slate-400 leading-tight">
                {roles[0]?.replace(/_/g, " ") || "Admin"}
              </span>
            </div>
            <ChevronDownIcon aria-hidden="true" className="h-3.5 w-3.5 stroke-[2.2] text-slate-400" />
          </button>

          {showProfileOptions && (
            <div className="absolute right-0 mt-2 w-52 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1.5 shadow-xl z-50">
              <div className="px-3.5 py-2 border-b border-slate-100">
                <p className="text-xs font-bold text-slate-900 truncate">{userName}</p>
                <p className="text-[11px] text-slate-500 truncate">{user?.email || "admin@example.com"}</p>
              </div>

              {canOpenSettings && (
                <button
                  type="button"
                  onClick={() => {
                    setShowProfileOptions(false);
                    navigate("/admin/settings");
                  }}
                  className="flex w-full items-center gap-2 px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Account Settings
                </button>
              )}

              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 border-t border-slate-100 px-3.5 py-2 text-xs font-bold text-rose-600 transition hover:bg-rose-50"
              >
                <LogoutIcon aria-hidden="true" className="h-4 w-4 stroke-[2]" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
